// Error codes: 'too-many-requests' (rate-limited or cooling down — check
// `retryAfterSeconds` on the thrown error), 'forbidden' (SMS only — blocked
// by the dev/stage environment allowlist), 'invalid-credentials' (identify:
// unknown/deactivated email; verify: wrong/expired code or no matching
// active user — deliberately not distinguished).
import { customerApi } from '@/globals/typescript/utils/api/customerClient'

export class CodeAuthGatewayError extends Error {
  retryAfterSeconds?: number

  constructor(code: string, retryAfterSeconds?: number) {
    super(code)
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/**
 * Why SMS cannot be used: the user has not yet logged in with an email code — the first login has
 * to be by email, which is what proves the mailbox works (MIL-579) — or SMS is restricted in this
 * environment.
 */
export type TSmsUnavailableReason = 'email-not-verified' | 'not-allowed-here'

export type TIdentifyChannels = {
  emailCode: true
  sms:
    | { maskedPhone: string; available: true }
    | { maskedPhone: string; available: false; reason: TSmsUnavailableReason }
    | null
}

export async function identify(email: string): Promise<TIdentifyChannels> {
  const result = await customerApi('post', 'auth/identify', {
    body: { email },
  })

  if (!result.success) throw new CodeAuthGatewayError(result.code)

  return result.data.channels as TIdentifyChannels
}

export async function sendSmsCode(email: string): Promise<void> {
  const result = await customerApi('post', 'auth/send-sms-code', {
    body: { email },
  })

  if (!result.success) {
    throw new CodeAuthGatewayError(result.code, 'retryAfterSeconds' in result ? result.retryAfterSeconds : undefined)
  }
}

export async function verifySmsCode(email: string, code: string): Promise<string> {
  const result = await customerApi('post', 'auth/verify-sms-code', {
    body: { email, code },
  })

  if (!result.success) throw new CodeAuthGatewayError(result.code)

  return result.data.customToken as string
}

export async function sendEmailCode(email: string): Promise<void> {
  const result = await customerApi('post', 'auth/send-email-code', {
    body: { email },
  })

  if (!result.success) {
    throw new CodeAuthGatewayError(result.code, 'retryAfterSeconds' in result ? result.retryAfterSeconds : undefined)
  }
}

export async function verifyEmailCode(email: string, code: string): Promise<string> {
  const result = await customerApi('post', 'auth/verify-email-code', {
    body: { email, code },
  })

  if (!result.success) throw new CodeAuthGatewayError(result.code)

  return result.data.customToken as string
}
